import { useState, useContext, useRef, useEffect, memo } from 'react';
import type { RefObject } from 'react';

import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@classify/react-core-components/styles';
import { styled } from '@classify/react-core-components/styles';

import TocTogglerButton from './TocTogglerButton';
import TableOfContent from './TableOfContent';
import CourseContext from '../../contexts/courseContext';
import useScrollAnimation, { ANIMATION_DURATION } from '../../hooks/useScrollAnimation';

export interface TocTogglerProps {
  onItemClick?: () => void;
  headerRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SEPARATOR_WIDTH = 32;

const Separator = styled('div')(({ theme: { breakpoints, palette, zIndex } }) => ({
  display: 'none',

  [breakpoints.up('sm')]: {
    display: 'block',
    width: SEPARATOR_WIDTH,
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: zIndex.fab,
    borderRight: `1px solid ${palette.layout.line.divider}`,
  },
}));

const Container = styled('div')(({ theme: { spacing, zIndex, breakpoints } }) => ({
  display: 'inline-block',
  height: 48,
  right: 0,
  zIndex: zIndex.fab,
  transition: `right ${ANIMATION_DURATION}ms`,
  bottom: spacing(15),
  position: 'fixed',
  [breakpoints.up('sm')]: {
    position: 'sticky',
    paddingLeft: spacing(4),
    paddingTop: spacing(5),
  },
}));

const CustomDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'hasVisibleHeader' })<{
  hasVisibleHeader: boolean;
  open: boolean;
}>(({ theme: { breakpoints, zIndex }, hasVisibleHeader, open }) => {
  const width = open ? '400px' : '0px';

  return {
    [breakpoints.up('sm')]: {
      width,
    },

    '& .MuiDrawer-paper': {
      width: '100%',

      [breakpoints.up('sm')]: {
        width,
        height: '100vh',
        position: hasVisibleHeader ? 'absolute' : 'fixed',
        zIndex: zIndex.fab,
      },
    },
  };
});

const TocToggler = memo(({ onItemClick, headerRef, isOpen, setIsOpen }: TocTogglerProps) => {
  const [hasVisibleHeader, setHasVisibleHeader] = useState(true);
  const tocRef = useRef<HTMLElement>(null);
  const { course } = useContext(CourseContext);
  const { breakpoints } = useTheme();

  const isDesktop = useMediaQuery(breakpoints.up('sm'));
  const position = isDesktop ? 'left' : 'right';

  useEffect(
    function setOnClickAndScrollListeners() {
      const setHeaderVisibility = () => {
        if (!headerRef.current) return;

        const headerAndNavbarHeight = headerRef.current.clientHeight * 2;

        setHasVisibleHeader(window.scrollY < headerAndNavbarHeight);
      };

      window.addEventListener('scroll', setHeaderVisibility);

      return () => {
        window.removeEventListener('scroll', setHeaderVisibility);
      };
    },
    [headerRef]
  );

  const [offset, setOffset] = useScrollAnimation({
    toggleVisibilityCallback: (isVisible: boolean) => {
      if (isVisible) {
        setOffset('0px');
      } else if (tocRef && tocRef.current) {
        setOffset(`-${tocRef.current.clientWidth}px`);
      }
    },
  });

  if (!course) return null;

  const { categoryColor } = course;
  const headerHeight = headerRef?.current?.clientHeight || 70;

  const handleItemClick = () => {
    if (!isDesktop) {
      setIsOpen(false);
    }

    if (onItemClick) {
      onItemClick();
    }
  };

  return (
    <div>
      <Separator />
      <div ref={tocRef}>
        <Container
          style={{
            [position]: isDesktop ? `${SEPARATOR_WIDTH}px` : offset,
            top: isDesktop ? `${headerHeight}px` : 'auto',
          }}
          data-testid="tocToggler"
        >
          <TocTogglerButton
            onClick={() => {
              setIsOpen(true);
            }}
            categoryColor={categoryColor}
          />
        </Container>
      </div>
      <CustomDrawer
        variant="persistent"
        anchor={position}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        hasVisibleHeader={hasVisibleHeader}
      >
        <TableOfContent
          onClose={() => {
            setIsOpen(false);
          }}
          onClickItem={handleItemClick}
          color={categoryColor}
        />
      </CustomDrawer>
    </div>
  );
});

export default TocToggler;
